import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import LazyDropdown from '../formsUi/dropdown/LazyDropdown';
import Label from '../formsUi/common/label/Label';
import ValidationMessage from '../formsUi/common/validation/ValidationMessage';

const resolveSelectedOption = (value, options = []) => {
    if (!value) return null;
    if (typeof value === 'object' && value?.value != null) {
        return options.find((opt) => String(opt.value) === String(value.value)) ?? value;
    }
    return options.find((opt) => String(opt.value) === String(value)) ?? null;
};

/**
 * FormLazyDropdownControls
 * ─────────────────────────────────────────────────────────────────────────────
 * Self-contained lazy-loading dropdown field control.
 * ALL pagination, search, loading state, and **prefill resolution** lives here.
 *
 * ── Loading UX ───────────────────────────────────────────────────────────────
 *   • Initial load  → react-select spinner in the control (isLoading)
 *   • Load more     → smooth footer loader below existing options (isLoadingMore)
 *     Both guards prevent concurrent / double-fetch calls.
 *
 * ── Debouncing ───────────────────────────────────────────────────────────────
 *   • Scroll-to-bottom   → 400ms debounce (prevents rapid-fire from fast scrollers)
 *   • Search input       → 300ms debounce inside LazyDropdown itself
 *
 * ── Prefill ──────────────────────────────────────────────────────────────────
 *   When a value is present but not found in the currently loaded options, the
 *   component auto-resolves it using:
 *     1. The value itself, if it's already a { value, label } object
 *     2. fieldSetting.fetchOptionById(id), if provided
 *     3. fieldSetting.fetchOptions({ resolveValue: id }) as a fallback
 *   This lets prefilled raw IDs (e.g. from a student-details API) display
 *   correctly even when the option is on a later page of the paginated set.
 *
 * ── Dependency reset ─────────────────────────────────────────────────────────
 *   When `fetchOptions` reference changes (e.g. institute re-keyed on university),
 *   the component automatically resets and re-fetches from page 0.
 *
 * ── Parent contract ──────────────────────────────────────────────────────────
 *   Preferred: use createLazyFetchOptions from FieldHelpers for minimal code.
 *   Manual:    fieldSetting.fetchOptions = async ({ page, search, resolveValue }) => { items, hasMore }
 *
 * DropdownSelect.jsx, DropDown.jsx, FormDropdownControls.jsx → NOT modified.
 */
const FormLazyDropdownControls = ({
    dataField,
    labelName,
    value,
    error,
    formSetting,
    overRideProps,
    isRequired,
    fieldSetting,
    onChange,
    onValidation,
}) => {
    const {
        fetchOptions,
        fetchOptionById,   // optional: async (id) => { value, label, ...meta } for prefill resolution
        resetKey,          // change this value to force a reset + re-fetch (e.g. selectedUniversityId)
        dependentValues,   // auto-injected by FormFieldsControl from dependsOn config
        placeholder,
        isMultiSelect,
        isDisable,
        isSearchable,
        subTitle,
    } = fieldSetting || {};

    // ── Internal state ─────────────────────────────────────────────────────────
    const [loadedOptions, setLoadedOptions] = useState([]);
    const [currentPage,   setCurrentPage]   = useState(0);
    const [isLoading,     setIsLoading]     = useState(false);   // initial load
    const [isLoadingMore, setIsLoadingMore] = useState(false);   // subsequent pages
    const [hasMore,       setHasMore]       = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);    // true until first fetch done

    const searchTermRef      = useRef('');
    const loadMoreTimerRef   = useRef(null);  // debounce for scroll-to-bottom
    const prefillResolvedRef = useRef(new Set()); // track IDs already resolved for prefill
    const prevResetKeyRef    = useRef(resetKey);  // detects a parent (dependsOn) value change

    // ── Core fetch ─────────────────────────────────────────────────────────────
    const doFetch = useCallback(async (page, search, reset = false) => {
        if (!fetchOptions) return;

        // Use the right loading flag based on whether this is the first page
        const isFirst = page === 0;
        if (isFirst) {
            setIsLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            // Pass dependentValues so the fetch fn can access parent field values
            // (e.g. universityId for institute dropdown) without any ref in the parent.
            const { items = [], hasMore: more = false } = await fetchOptions({
                page, search, dependentValues,
            });

            setLoadedOptions((prev) => (reset || isFirst ? items : [...prev, ...items]));
            setHasMore(more);
            setCurrentPage(page);

            // After the first successful fetch, mark initial load as done
            if (isFirst) setIsInitialLoad(false);
        } catch (_) {
            // Let the parent's API hook handle error toasts / logging.
        } finally {
            if (isFirst) {
                setIsLoading(false);
            } else {
                setIsLoadingMore(false);
            }
        }
    }, [fetchOptions, dependentValues]);

    // ── Reset + reload when fetchOptions or resetKey changes ─────────────────────
    // resetKey: parent can pass any value (e.g. selectedUniversityId) to force a
    // reset without needing to create a new fetchOptions reference.  This lets
    // fetchOptions stay permanently stable and avoids config rebuilds that would
    // trigger FormBuilder's configInitialState useEffect (which wipes field values).
    useEffect(() => {
        if (!fetchOptions) return;

        // Cancel any pending debounced scroll call
        clearTimeout(loadMoreTimerRef.current);

        // Clear prefill tracking so a new fetchOptions can re-resolve if needed
        prefillResolvedRef.current = new Set();

        // A changed parent value makes the current selection stale (e.g. a district of the old state)
        if (prevResetKeyRef.current !== resetKey) {
            prevResetKeyRef.current = resetKey;
            const hasValue = Array.isArray(value) ? value.length > 0 : (value !== null && value !== undefined && value !== '');
            if (hasValue) onChange?.(dataField, isMultiSelect ? [] : null);
        }

        searchTermRef.current = '';
        setLoadedOptions([]);
        setCurrentPage(0);
        setHasMore(true);
        setIsInitialLoad(true);
        doFetch(0, '', true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchOptions, resetKey]);

    // ── Prefill resolution ──────────────────────────────────────────────────
    // When a value is set (e.g. from a student-details API) but isn't in the
    // currently loaded paginated options, auto-resolve it so the dropdown
    // can display the correct label.
    //
    // Resolution order:
    //   1. Value is { value, label } object → inject directly (no API call)
    //   2. fetchOptionById provided        → call it (simple single-item API)
    //   3. fetchOptions({ resolveValue })  → fallback using paginated callback
    useEffect(() => {
        if (isMultiSelect) return; // multi-select prefill handled separately

        const rawId = value && typeof value === 'object' ? value.value : value;
        if (!rawId || prefillResolvedRef.current.has(String(rawId))) return;

        // Already in loaded options? Nothing to do.
        if (loadedOptions.some(opt => String(opt.value) === String(rawId))) return;

        // Strategy 1: value is already { value, label } → inject directly
        if (typeof value === 'object' && value.label) {
            prefillResolvedRef.current.add(String(rawId));
            setLoadedOptions(prev => {
                if (prev.some(o => String(o.value) === String(rawId))) return prev;
                return [value, ...prev];
            });
            return;
        }

        // Strategy 2/3: resolve via fetchOptionById or fetchOptions({ resolveValue })
        const resolveFn = fetchOptionById
            ? () => fetchOptionById(rawId)
            : fetchOptions
                ? () => fetchOptions({ resolveValue: rawId }).then(r => r?.items?.[0] ?? null)
                : null;

        if (!resolveFn) return;

        let cancelled = false;
        resolveFn()
            .then(option => {
                if (cancelled || !option) return;
                prefillResolvedRef.current.add(String(rawId));
                setLoadedOptions(prev => {
                    if (prev.some(o => String(o.value) === String(option.value))) return prev;
                    return [option, ...prev];
                });
                if (onChange) {
                    onChange(dataField, option);
                }
            })
            .catch(() => {
                // Silently fail — the dropdown will show blank, which is the
                // same behaviour as before this feature was added.
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, fetchOptions, fetchOptionById]);

    // ── Scroll-to-bottom → next page (400 ms debounced) ───────────────────────
    const handleLoadMore = useCallback(() => {
        // Guard: don't fire if no more pages or any fetch is in flight
        if (!hasMore || isLoading || isLoadingMore) return;

        clearTimeout(loadMoreTimerRef.current);
        loadMoreTimerRef.current = setTimeout(() => {
            doFetch(currentPage + 1, searchTermRef.current);
        }, 400);
    }, [hasMore, isLoading, isLoadingMore, currentPage, doFetch]);

    // ── User typed → server search (resets list, debounced inside LazyDropdown) ─
    const handleSearch = useCallback((term) => {
        // Cancel pending scroll debounce first
        clearTimeout(loadMoreTimerRef.current);

        searchTermRef.current = term;
        setLoadedOptions([]);
        setCurrentPage(0);
        setHasMore(true);
        setIsInitialLoad(true);
        doFetch(0, term, true);
    }, [doFetch]);

    // ── Change handler ─────────────────────────────────────────────────────────
    const handleChange = useCallback((selectedOption) => {
        if (!onChange) return;
        if (isMultiSelect) {
            onChange(dataField, selectedOption.map((opt) => opt.value));
        } else {
            onChange(dataField, selectedOption);
        }
    }, [onChange, dataField, isMultiSelect]);

    const handleOnBlur = useCallback(() => {
        if (onValidation) onValidation(dataField);
    }, [onValidation, dataField]);

    // ── Menu Open handler ──────────────────────────────────────────────────────
    // Refetches the full list when refreshOnOpen is set, or when an earlier search is still applied:
    // picking an option (or clearing with X) empties the input without an 'input-change' event, so the
    // options would otherwise stay filtered to that old search and only the old match would show.
    const handleMenuOpen = useCallback(() => {
        if (fieldSetting?.refreshOnOpen || searchTermRef.current) {
            clearTimeout(loadMoreTimerRef.current);
            prefillResolvedRef.current = new Set();
            searchTermRef.current = '';
            setLoadedOptions([]);
            setCurrentPage(0);
            setHasMore(true);
            setIsInitialLoad(true);
            doFetch(0, '', true);
        }
    }, [doFetch, fieldSetting?.refreshOnOpen]);

    // ── Resolve selected value → option object ─────────────────────────────────
    const resolvedValue = isMultiSelect
        ? Array.isArray(value)
            ? loadedOptions.filter((opt) => value.includes(opt.value))
            : []
        : resolveSelectedOption(value, loadedOptions);

    // ── Disabled state (same logic as FormDropdownControls) ────────────────────
    const isDropdownDisabled =
        formSetting?.isViewOnly ||
        (overRideProps?.isDisable !== false && isDisable) ||
        false;

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="dropdown-label">
            <div className="input-label-part">
                {labelName && labelName !== '' && (
                    <Label labelName={labelName} isRequired={isRequired} />
                )}
                {subTitle && (
                    <div className="section-title"><h5>{subTitle}</h5></div>
                )}
                <span className="dropdown-with-btn">
                    <LazyDropdown
                        placeholder={placeholder}
                        isMultiSelect={isMultiSelect}
                        isSearchable={isSearchable !== false}
                        isClearable={fieldSetting?.isClearable ?? true}
                        optionsValue={loadedOptions}
                        value={resolvedValue}
                        handleDropdownChange={handleChange}
                        handleDropdownBlur={handleOnBlur}
                        onMenuOpen={handleMenuOpen}
                        isDropdownDisabled={isDropdownDisabled}
                        isLoading={isInitialLoad && isLoading}
                        isLoadingMore={!isInitialLoad && isLoadingMore}
                        hasMore={hasMore}
                        onLoadMore={handleLoadMore}
                        onSearch={handleSearch}
                    />
                </span>
            </div>
            <ValidationMessage error={error || ''}
                helperText={fieldSetting?.helperText}
                successText={overRideProps?.successText ?? fieldSetting?.successText} />
        </div>
    );
};

FormLazyDropdownControls.propTypes = {
    dataField  : PropTypes.string.isRequired,
    labelName  : PropTypes.string,
    value      : PropTypes.any,
    error      : PropTypes.string,
    formSetting: PropTypes.shape({ isViewOnly: PropTypes.bool }),
    fieldSetting: PropTypes.shape({
        fetchOptions    : PropTypes.func,
        fetchOptionById : PropTypes.func,
        placeholder     : PropTypes.string,
        subTitle        : PropTypes.string,
        isMultiSelect   : PropTypes.bool,
        isSearchable    : PropTypes.bool,
        isDisable       : PropTypes.bool,
    }),
    overRideProps: PropTypes.object,
    isRequired   : PropTypes.bool,
    onChange     : PropTypes.func.isRequired,
    onValidation : PropTypes.func,
};

export default FormLazyDropdownControls;
